import { UnknownAction, ThunkAction, configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { createTransform } from "redux-persist";
import authReducer from "./slices/authSlice";
import layoutReducer from "./slices/layoutSlice";
import navigationReducer from "./slices/navigationSlice";
import formsReducer from "./slices/formsSlice";
import notificationsReducer from "./slices/notificationsSlice";
import lookupsReducer from "./slices/lookupsSlice";
import pdfViewerReducer from "./slices/pdfViewerSlice";
import htmlDrawerReducer from "./slices/htmlDrawerSlice";
import confirmationDialogReducer from "./slices/confirmationDialogSlice";
import settingsReducer from "./slices/settingsSlice";
import screenPopupReducer from "./slices/screenPopupSlice";
import profileEditDrawerReducer from "./slices/profileEditDrawerSlice";
import jobcardManpowerModalReducer from "./slices/jobcardManpowerModalSlice";
import billReleaseLinkedModalReducer from "./slices/billReleaseLinkedModalSlice";

const PAYROLL_SUMMARY_SCREEN_ID = "2231";

/** Not persisted — refetched from API on load; selection scope is session-only. */
const PAYROLL_TRANSIENT_FORM_KEYS = [
  "payroll_stats_scope",
  "total_payout",
  "pending_approvals",
  "employees_processed",
  "payroll_selected_employee_ids",
] as const;

function stripPayrollTransientFormData(
  formDataByScreen: Record<string, Record<string, unknown>> | undefined
): Record<string, Record<string, unknown>> {
  if (!formDataByScreen) return formDataByScreen ?? {};
  const next = { ...formDataByScreen };
  for (const [screenId, data] of Object.entries(next)) {
    if (!data || typeof data !== "object") continue;
    const isPayrollScreen =
      screenId === PAYROLL_SUMMARY_SCREEN_ID ||
      typeof data.payroll_month_filter === "string" ||
      typeof data.payroll_month === "string";
    if (!isPayrollScreen) continue;
    const cleaned = { ...data };
    for (const key of PAYROLL_TRANSIENT_FORM_KEYS) {
      delete cleaned[key];
    }
    next[screenId] = cleaned;
  }
  return next;
}

const payrollFormTransform = createTransform(
  stripPayrollTransientFormData,
  stripPayrollTransientFormData,
  { whitelist: ["formDataByScreen"] }
);

// Persist configuration for forms - only persist formDataByScreen
const formsPersistConfig = {
  key: "forms",
  storage,
  whitelist: ["formDataByScreen"], // Only persist form data, not loading states
  transforms: [payrollFormTransform],
};

// Persist configuration for lookups - persist all lookup data
// const lookupsPersistConfig = {
//   key: "lookups",
//   storage,
//   whitelist: ["lookups", "lastFetched", "rawData"], // Persist lookup data and timestamps
// };

// Persist configuration for auth
const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["user", "isAuthenticated"], // Persist user and auth status
};

// Persist configuration for layout
const layoutPersistConfig = {
  key: "layout",
  storage,
  whitelist: ["currentBranch", "availableBranches"], // Persist branch info
};

const navigationPersistConfig = {
  key: "navigation",
  storage,
  whitelist: ["currentView", "previousView", "history", "refreshTimestamp"], // Persist navigation info
};

// Create persisted reducers
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedLayoutReducer = persistReducer(layoutPersistConfig, layoutReducer);
const persistedFormsReducer = persistReducer(formsPersistConfig, formsReducer);
// const persistedLookupsReducer = persistReducer(
//   lookupsPersistConfig,
//   lookupsReducer
// );
const persistedNavigationReducer = persistReducer(navigationPersistConfig, navigationReducer);
// Combine all reducers
const rootReducer = combineReducers({
  auth: persistedAuthReducer,
  layout: persistedLayoutReducer,
  navigation: persistedNavigationReducer,
  forms: persistedFormsReducer,
  notifications: notificationsReducer,
  lookups: lookupsReducer,
  pdfViewer: pdfViewerReducer,
  htmlDrawer: htmlDrawerReducer,
  confirmationDialog: confirmationDialogReducer,
  settings: settingsReducer,
  screenPopup: screenPopupReducer,
  profileEditDrawer: profileEditDrawerReducer,
  jobcardManpowerModal: jobcardManpowerModalReducer,
  billReleaseLinkedModal: billReleaseLinkedModalReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "confirmationDialog/showConfirmationDialog",
        ],
        ignoredActionsPaths: ["payload.resolve"],
      },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  undefined,
  UnknownAction
>;

